import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Gamepad2, Plus, Trash2, Upload, Loader2, Megaphone, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, GameCard, Spinner, EmptyState } from "@/components/Bits";
import ShareGameCard from "@/components/ShareGameCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function MyGames() {
  const navigate = useNavigate();
  const [games, setGames] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/games").then((r) => setGames(r.data));
  useEffect(() => { load(); }, []);

  const importProject = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const { data } = await api.post("/projects/import", { bundle });
      toast.success(`Imported "${data.title}" with ${data.level_count} levels!`);
      navigate(`/app/project/${data.id}`);
    } catch (err) {
      toast.error(err.name === "SyntaxError" ? "That doesn't look like a valid project file." : formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  if (!games) return <Spinner />;
  const term = q.trim().toLowerCase();
  const filtered = games.filter((g) => (filter === "all" || (filter === "published" ? g.status === "published" : g.status !== "published"))
    && (!term || (g.title || "").toLowerCase().includes(term)));

  const del = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this game?")) return;
    await api.delete(`/games/${id}`);
    toast.success("Game deleted");
    load();
  };

  const badge = (s) => ({ published: "LIVE", pending_approval: "WAITING", pending_moderation: "IN REVIEW", rejected: "FIX", draft: "DRAFT" }[s] || "DRAFT");

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto">
      <PageHeader title="My Games" icon={Gamepad2} subtitle="All your creations in one place."
        action={<div className="flex gap-2">
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={importProject} className="hidden" data-testid="import-project-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={importing} variant="outline" data-testid="mygames-import" className="rounded-full">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import</Button>
          <Button onClick={() => navigate("/app/create")} data-testid="mygames-create" className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]"><Plus className="w-4 h-4" /> New Game</Button>
        </div>} />

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-5">
        <Tabs value={filter} onValueChange={setFilter} className="shrink-0">
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-all">All ({games.length})</TabsTrigger>
            <TabsTrigger value="published" data-testid="filter-published">Published</TabsTrigger>
            <TabsTrigger value="drafts" data-testid="filter-drafts">Drafts</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative sm:ml-auto sm:w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your games…" className="pl-9 rounded-full" data-testid="mygames-search" />
        </div>
      </div>

      {games.length > 0 && filtered.length === 0 ? (
        <EmptyState title="No matches" text={`No games match "${q}".`} />
      ) : filtered.length === 0 ? (
        <EmptyState title="Nothing here yet" text="Create a game to get started." action={<Button onClick={() => navigate("/app/create")} className="rounded-full"><Plus className="w-4 h-4" /> Create</Button>} />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {filtered.map((g) => (
            <div key={g.id} className="relative group">
              <GameCard game={{ ...g, world: g.config?.world }} onClick={() => navigate(`/app/build/${g.id}`)} badge={g.shared ? "SHARED" : badge(g.status)} noShare />
              <ShareGameCard game={{ ...g, world: g.config?.world }} gameId={g.id} trigger={
                <button data-testid={`mygames-share-${g.id}`} onClick={(e) => e.stopPropagation()} title="Share game"
                  className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/50 text-white grid place-items-center opacity-0 group-hover:opacity-100 hover:bg-[hsl(var(--primary))] transition-all">
                  <Megaphone className="w-4 h-4" />
                </button>
              } />
              <button onClick={(e) => del(e, g.id)} data-testid={`delete-${g.id}`}
                className={`absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white grid place-items-center opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all ${g.shared ? "hidden" : ""}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
